import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Trophy, Medal, TrendingUp, Users, School, Star,
  Search, Filter, ChevronLeft, Award, Target, Zap, Crown,
  BookOpen, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  tier_5: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/40', label: 'Champion' },
  tier_4: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/40', label: 'High Performance' },
  tier_3: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40', label: 'Elite' },
  tier_2: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/40', label: 'Performance' },
  tier_1: { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/40', label: 'Development' },
};

const RANK_ICONS = [
  null,
  <Crown className="w-5 h-5 text-yellow-400" />,
  <Medal className="w-5 h-5 text-gray-300" />,
  <Medal className="w-5 h-5 text-amber-600" />,
];

export default function RankingsPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('athletes');
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [athleteRankings, setAthleteRankings] = useState([]);
  const [schoolRankings, setSchoolRankings] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [talentPool, setTalentPool] = useState([]);
  const [eligibleAthletes, setEligibleAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sportFilter, setSportFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPointsRef, setShowPointsRef] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (sportFilter !== 'all') params.sport = sportFilter;
      if (ageFilter !== 'all') params.age_group = ageFilter;

      const [configRes, statsRes, athleteRes, schoolRes, ratedRes, talentRes, eligibleRes] = await Promise.all([
        axios.get(`${API_URL}/api/rankings/config`),
        axios.get(`${API_URL}/api/rankings/stats/overview`),
        axios.get(`${API_URL}/api/rankings/athletes`, { params: { ...params, limit: 100 } }),
        axios.get(`${API_URL}/api/rankings/schools`, { params: { limit: 100 } }),
        axios.get(`${API_URL}/api/rankings/ratings/top`, { params: { ...params, limit: 50 } }),
        axios.get(`${API_URL}/api/rankings/talent-pool`, { params }),
        axios.get(`${API_URL}/api/rankings/sponsorship/eligible-athletes`, { params: { ...params, limit: 100 } }),
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
      setAthleteRankings(athleteRes.data.rankings || []);
      setSchoolRankings(schoolRes.data.rankings || []);
      setTopRated(ratedRes.data.top_rated || []);
      setTalentPool(talentRes.data.talent_pool || []);
      setEligibleAthletes(eligibleRes.data.eligible_athletes || []);
    } catch (err) {
      console.error('Failed to fetch rankings:', err);
    }
    setLoading(false);
  }, [sportFilter, ageFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredAthletes = athleteRankings.filter(a =>
    !searchQuery || (a.athlete_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.school || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredSchools = schoolRankings.filter(s =>
    !searchQuery || (s.school_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="rankings-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0f1923] to-[#0a0a0f] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <Trophy className="w-6 h-6 text-yellow-400" />
            <h1 className="text-xl font-bold text-white tracking-tight">National Rankings</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setShowPointsRef(!showPointsRef)}
              className="border-white/10 text-white/70 hover:text-white"
              data-testid="btn-user-points-ref"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              {showPointsRef ? 'Hide' : 'Points System'}
              {showPointsRef ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </Button>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="border-white/10 text-white/70 hover:text-white">Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Points Reference Dropdown */}
        {showPointsRef && config && (
          <UserPointsReference config={config} />
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="rankings-stats">
            {[
              { label: 'Ranked Athletes', value: stats.ranked_athletes, icon: Users, color: 'text-blue-400' },
              { label: 'ELO Rated', value: stats.rated_athletes, icon: TrendingUp, color: 'text-purple-400' },
              { label: 'Results Recorded', value: stats.competition_results, icon: Trophy, color: 'text-yellow-400' },
              { label: 'Talent Pool', value: stats.talent_pool_count, icon: Star, color: 'text-emerald-400' },
              { label: 'Schools Ranked', value: stats.schools_ranked, icon: School, color: 'text-cyan-400' },
              { label: 'Sponsorship Eligible', value: stats.sponsorship_eligible, icon: Award, color: 'text-orange-400' },
            ].map((s, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-xs text-white/40">{s.label}</span>
                </div>
                <p className="text-xl font-bold text-white">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              placeholder="Search athletes, schools..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="rankings-search"
            />
          </div>
          <Select value={sportFilter} onValueChange={setSportFilter}>
            <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white" data-testid="rankings-sport-filter">
              <SelectValue placeholder="All Sports" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {(config?.supported_sports || []).map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ageFilter} onValueChange={setAgeFilter}>
            <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white" data-testid="rankings-age-filter">
              <SelectValue placeholder="All Ages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {(config?.age_groups || []).map(ag => (
                <SelectItem key={ag.age_group_name} value={ag.age_group_name}>{ag.age_group_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="athletes" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400 text-white/60" data-testid="tab-athletes">
              <Trophy className="w-4 h-4 mr-1.5" /> Athletes
            </TabsTrigger>
            <TabsTrigger value="schools" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-white/60" data-testid="tab-schools">
              <School className="w-4 h-4 mr-1.5" /> Schools
            </TabsTrigger>
            <TabsTrigger value="elo" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-white/60" data-testid="tab-elo">
              <TrendingUp className="w-4 h-4 mr-1.5" /> ELO Ratings
            </TabsTrigger>
            <TabsTrigger value="talent" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-white/60" data-testid="tab-talent">
              <Star className="w-4 h-4 mr-1.5" /> Talent Pool
            </TabsTrigger>
            <TabsTrigger value="sponsorship" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 text-white/60" data-testid="tab-sponsorship">
              <Award className="w-4 h-4 mr-1.5" /> Sponsorship
            </TabsTrigger>
          </TabsList>

          {/* ATHLETE RANKINGS */}
          <TabsContent value="athletes" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-white/40">Loading rankings...</div>
            ) : filteredAthletes.length === 0 ? (
              <EmptyState icon={Trophy} title="No Rankings Yet" subtitle="Competition results must be submitted to generate rankings." />
            ) : (
              <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden" data-testid="athlete-rankings-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 text-white/40 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left">Rank</th>
                      <th className="px-4 py-3 text-left">Athlete</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">School</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">Sport</th>
                      <th className="px-4 py-3 text-left hidden lg:table-cell">Age Group</th>
                      <th className="px-4 py-3 text-right">Points</th>
                      <th className="px-4 py-3 text-center hidden md:table-cell">Comps</th>
                      <th className="px-4 py-3 text-center">Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAthletes.map((a, i) => (
                      <tr key={a.athlete_id + a.sport} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {RANK_ICONS[a.rank_position] || <span className="text-white/50 text-sm w-5 text-center">{a.rank_position}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">{a.athlete_name}</span>
                            <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded">{a.age_group}</span>
                          </div>
                          <div className="text-white/30 text-xs md:hidden">{a.school || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-white/60 text-sm hidden md:table-cell">{a.school || '—'}</td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs bg-white/5 text-white/60 px-2 py-0.5 rounded">{a.sport}</span>
                        </td>
                        <td className="px-4 py-3 text-white/50 text-sm hidden lg:table-cell">{a.age_group}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-white font-bold text-sm">{a.total_points.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-white/50 text-sm hidden md:table-cell">{a.competitions_count}</td>
                        <td className="px-4 py-3 text-center">
                          {a.sponsorship_tier ? (
                            <span className={`text-xs px-2 py-0.5 rounded border ${TIER_COLORS[a.sponsorship_tier]?.bg} ${TIER_COLORS[a.sponsorship_tier]?.text} ${TIER_COLORS[a.sponsorship_tier]?.border}`}>
                              {TIER_COLORS[a.sponsorship_tier]?.label}
                            </span>
                          ) : (
                            <span className="text-white/20 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* SCHOOL RANKINGS */}
          <TabsContent value="schools" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-white/40">Loading...</div>
            ) : filteredSchools.length === 0 ? (
              <EmptyState icon={School} title="No School Rankings" subtitle="School rankings are generated from athlete competition results." />
            ) : (
              <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden" data-testid="school-rankings-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 text-white/40 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left">Rank</th>
                      <th className="px-4 py-3 text-left">School</th>
                      <th className="px-4 py-3 text-right">Points</th>
                      <th className="px-4 py-3 text-center">Athletes</th>
                      <th className="px-4 py-3 text-center hidden md:table-cell">Gold</th>
                      <th className="px-4 py-3 text-center hidden md:table-cell">Silver</th>
                      <th className="px-4 py-3 text-center hidden md:table-cell">Bronze</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSchools.map((s) => (
                      <tr key={s.school_name} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                        <td className="px-4 py-3">
                          {RANK_ICONS[s.rank_position] || <span className="text-white/50 text-sm">{s.rank_position}</span>}
                        </td>
                        <td className="px-4 py-3 text-white font-medium text-sm">{s.school_name}</td>
                        <td className="px-4 py-3 text-right text-white font-bold text-sm">{s.total_points.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center text-white/50 text-sm">{s.athletes_count}</td>
                        <td className="px-4 py-3 text-center text-yellow-400 text-sm hidden md:table-cell">{s.gold_medals}</td>
                        <td className="px-4 py-3 text-center text-gray-300 text-sm hidden md:table-cell">{s.silver_medals}</td>
                        <td className="px-4 py-3 text-center text-amber-600 text-sm hidden md:table-cell">{s.bronze_medals}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* ELO RATINGS */}
          <TabsContent value="elo" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-white/40">Loading...</div>
            ) : topRated.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No ELO Ratings" subtitle="ELO ratings are calculated from head-to-head match results." />
            ) : (
              <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden" data-testid="elo-ratings-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 text-white/40 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left">#</th>
                      <th className="px-4 py-3 text-left">Athlete</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">Sport</th>
                      <th className="px-4 py-3 text-right">Rating</th>
                      <th className="px-4 py-3 text-right hidden md:table-cell">Peak</th>
                      <th className="px-4 py-3 text-center">W</th>
                      <th className="px-4 py-3 text-center">L</th>
                      <th className="px-4 py-3 text-center hidden md:table-cell">D</th>
                      <th className="px-4 py-3 text-center hidden md:table-cell">Played</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRated.map((r, i) => (
                      <tr key={r.athlete_id + r.sport} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                        <td className="px-4 py-3 text-white/50 text-sm">{i + 1}</td>
                        <td className="px-4 py-3 text-white font-medium text-sm">{r.athlete_name}</td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs bg-white/5 text-white/60 px-2 py-0.5 rounded">{r.sport}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-purple-400 font-bold text-sm">{r.current_rating}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-white/40 text-sm hidden md:table-cell">{r.peak_rating}</td>
                        <td className="px-4 py-3 text-center text-emerald-400 text-sm">{r.wins}</td>
                        <td className="px-4 py-3 text-center text-red-400 text-sm">{r.losses}</td>
                        <td className="px-4 py-3 text-center text-white/40 text-sm hidden md:table-cell">{r.draws}</td>
                        <td className="px-4 py-3 text-center text-white/50 text-sm hidden md:table-cell">{r.matches_played}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* TALENT POOL */}
          <TabsContent value="talent" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-white/40">Loading...</div>
            ) : talentPool.length === 0 ? (
              <EmptyState icon={Star} title="No Talent Pool Entries" subtitle="Athletes are flagged when they reach top 10 nationally, win championships, or achieve 1800+ rating." />
            ) : (
              <div className="grid gap-3" data-testid="talent-pool-list">
                {talentPool.map((t) => (
                  <div key={t.athlete_id + t.sport} className="bg-white/[0.03] border border-white/5 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-emerald-400" />
                        <span className="text-white font-medium">{t.athlete_name}</span>
                        <span className="text-xs bg-white/5 text-white/50 px-2 py-0.5 rounded">{t.sport}</span>
                        <span className="text-xs text-white/30">{t.age_group}</span>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        {(t.qualification_reason || []).map((reason, ri) => (
                          <span key={ri} className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded">
                            {reason.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      {t.rank_position && <div className="text-white/40 text-xs">Rank #{t.rank_position}</div>}
                      {t.total_points && <div className="text-white font-bold text-sm">{t.total_points.toLocaleString()} pts</div>}
                      {t.performance_rating && <div className="text-purple-400 text-xs">ELO {t.performance_rating}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SPONSORSHIP ELIGIBLE */}
          <TabsContent value="sponsorship" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-white/40">Loading...</div>
            ) : eligibleAthletes.length === 0 ? (
              <EmptyState icon={Award} title="No Sponsorship Eligible Athletes" subtitle="Athletes in the top 25% of their sport/age group qualify for sponsorship tiers." />
            ) : (
              <div className="space-y-4">
                {/* Tier Legend */}
                <div className="flex flex-wrap gap-2">
                  {Object.entries(TIER_COLORS).map(([key, val]) => (
                    <span key={key} className={`text-xs px-3 py-1 rounded border ${val.bg} ${val.text} ${val.border}`}>
                      {val.label}
                    </span>
                  ))}
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden" data-testid="sponsorship-table">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5 text-white/40 text-xs uppercase tracking-wider">
                        <th className="px-4 py-3 text-left">Rank</th>
                        <th className="px-4 py-3 text-left">Athlete</th>
                        <th className="px-4 py-3 text-left hidden md:table-cell">Sport</th>
                        <th className="px-4 py-3 text-right">Points</th>
                        <th className="px-4 py-3 text-center">Percentile</th>
                        <th className="px-4 py-3 text-center">Tier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligibleAthletes.map((a) => (
                        <tr key={a.athlete_id + a.sport} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                          <td className="px-4 py-3 text-white/50 text-sm">#{a.rank_position}</td>
                          <td className="px-4 py-3 text-white font-medium text-sm">{a.athlete_name}</td>
                          <td className="px-4 py-3 text-white/50 text-sm hidden md:table-cell">{a.sport}</td>
                          <td className="px-4 py-3 text-right text-white font-bold text-sm">{a.total_points.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center text-white/50 text-sm">Top {a.percentile_rank}%</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded border ${TIER_COLORS[a.sponsorship_tier]?.bg} ${TIER_COLORS[a.sponsorship_tier]?.text} ${TIER_COLORS[a.sponsorship_tier]?.border}`}>
                              {TIER_COLORS[a.sponsorship_tier]?.label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function UserPointsReference({ config }) {
  const levels = config?.competition_levels || {};
  const stages = config?.performance_stages || {};
  const awards = config?.player_awards || {};
  const tiers = config?.sponsorship_tiers || {};

  return (
    <div className="bg-gradient-to-r from-[#0f1923] to-[#0a0a0f] border border-yellow-500/20 rounded-lg p-5 space-y-4" data-testid="user-points-reference">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-yellow-400" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">How Points & Rankings Work</h3>
      </div>
      <p className="text-white/50 text-xs">
        <strong className="text-white/70">Formula:</strong> Total Points = Base Points × Competition Multiplier + Award Points × Competition Multiplier
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <h4 className="text-xs text-yellow-400 font-medium uppercase tracking-wider mb-2">Competition Levels</h4>
          <div className="space-y-1">
            {Object.entries(levels).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/60">{val.name || key}</span>
                <span className="text-yellow-400 font-bold">×{val.multiplier || val}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-blue-400 font-medium uppercase tracking-wider mb-2">Performance Stages</h4>
          <div className="space-y-1">
            {Object.entries(stages).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/60">{val.name || key}</span>
                <span className="text-blue-400 font-bold">{val.points || val} pts</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-purple-400 font-medium uppercase tracking-wider mb-2">Player Awards</h4>
          <div className="space-y-1">
            {Object.entries(awards).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/60">{typeof val === 'object' ? val.name : key.replace(/_/g, ' ')}</span>
                <span className="text-purple-400 font-bold">+{typeof val === 'object' ? val.points : val}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-orange-400 font-medium uppercase tracking-wider mb-2">Sponsorship Tiers</h4>
          <div className="space-y-1">
            {Object.entries(tiers).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/60">{val.name || key}</span>
                <span className="text-orange-400 font-bold">Top {val.min_percentile}–{val.max_percentile}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-white/30 text-xs">
        Example: Champion at National level = 200 × 6.0 = 1,200 pts. With Highest Scorer award: 1,200 + (40 × 6.0) = 1,440 pts.
      </p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center py-16 bg-white/[0.02] border border-white/5 rounded-lg">
      <Icon className="w-12 h-12 text-white/10 mx-auto mb-3" />
      <h3 className="text-white/60 font-medium">{title}</h3>
      <p className="text-white/30 text-sm mt-1 max-w-md mx-auto">{subtitle}</p>
    </div>
  );
}
